import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type {
  AmenityStatus,
  CheckInInstructions,
} from "@b2bportal/lodging-api";
import { AmenityIcon } from "@tenant/hotels";
import { Dialog, DialogContent, DialogTrigger } from "@tenant/ui";
import clsx from "clsx";
import { useHotelDetailsSection } from "./hooks";
import defaultStyles from "./HotelDetailsSection.module.scss";

export interface HotelDetailsSectionComponentProps
  extends PlatformComponentProps {
  amenities: AmenityStatus[];
  checkInInformation?: CheckInInstructions;
}

const COMPONENT_KEY = CoreHotelsComponent.Amenities;

export const HotelDetailsSection = (
  props: HotelDetailsSectionComponentProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { children, className } = props;

  const { context, text } = useHotelDetailsSection(props);

  return (
    <div className={clsx(block, className)}>
      {/* Check-in/Check-out Section */}
      {(context.showCheckInTime || context.showCheckOutTime) && (
        <div className={cn("check-in-section")}>
          {context.showCheckInTime && (
            <div className={cn("check-in-item")}>
              <h3 className={cn("check-in-title")}>{text.checkInTitle}</h3>
              <div className={cn("check-in-time")}>{text.checkInTime}</div>
            </div>
          )}
          {context.showCheckOutTime && (
            <div className={cn("check-in-item")}>
              <h3 className={cn("check-in-title")}>{text.checkOutTitle}</h3>
              <div className={cn("check-in-time")}>{text.checkOutTime}</div>
            </div>
          )}
        </div>
      )}

      {/* Amenities Section */}
      <div className={cn("amenities-section")}>
        <h3 className={cn("amenities-title")}>{text.amenitiesTitle}</h3>
        <ul className={cn("amenity-list")}>
          {context.amenitiesToShow.map(({ amenity, displayName }) => (
            <li key={amenity} className={cn("amenity-item")}>
              <AmenityIcon
                className={cn("amenity-icon")}
                amenity={amenity}
                alt={displayName}
              />
              {displayName}
            </li>
          ))}
        </ul>
        {context.showMoreAmenetiesLink && (
          <Dialog>
            <DialogTrigger className={cn("more-amenities")}>
              {text.moreAmenities}
            </DialogTrigger>
            <DialogContent title={text.amenitiesTitle}>
              <ul className={cn("dialog-amenity-list")}>
                {context.amenities.map(({ amenity, displayName }) => (
                  <li key={amenity} className={cn("amenity-item")}>
                    <AmenityIcon
                      className={cn("amenity-icon")}
                      amenity={amenity}
                      alt={displayName}
                    />
                    {displayName}
                  </li>
                ))}
              </ul>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {children}
    </div>
  );
};
